import { useState, useRef } from "react";
import { useApi } from "../../hooks/use-api";
import { useSingleton } from "../../hooks/use-singleton";

const useHandler = () => {
  const { get, post } = useApi();
  const { mail, user } = useSingleton();
  const [loading, setLoading] = useState(false);
  const [passage, setPassage] = useState(null);
  const [evaluation, setEvaluation] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const userEmail = mail || user?.data?.email || "";

  const fetchPassage = async (preferences) => {
    setLoading(true);
    try {
      const { level, topic, difficulty } = preferences;
      const url = `https://rovianai.com/secure-api/english/read-aloud/passage?class_level=${"10"}&difficulty=${difficulty}&category=${topic}`;
      const res = await get(url);
      if (res?.data) setPassage(res.data);
    } catch (e) {
      console.error("Fetch Passage Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
    } catch (e) {
      console.error("Error starting recording:", e);
    }
  };

  const stopRecording = () => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) return resolve(null);

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        // Stop all tracks to release the microphone
        mediaRecorderRef.current.stream
          .getTracks()
          .forEach((track) => track.stop());
        resolve(audioBlob);
      };

      mediaRecorderRef.current.stop();
    });
  };

  const evaluateSpeech = async (audioBlob) => {
    if (!passage || !userEmail || !audioBlob) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("passage_id", passage.passage_id);
      formData.append("audio", audioBlob, "recording.webm");
      formData.append("user_mail", userEmail);

      const res = await post(
        "https://rovianai.com/secure-api/english/read-aloud/evaluate",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (res?.data) {
        const data = res.data;

        const KNOWN_NON_METRIC_KEYS = [
          "message",
          "overall_score",
          "passage_id",
          "status",
          "audio_url",
          "transcription",
        ];

        const formatMetricLabel = (key) => key.replace(/_/g, " ").toUpperCase();

        const metrics = Object.entries(data)
          .filter(
            ([key, value]) =>
              !KNOWN_NON_METRIC_KEYS.includes(key) &&
              value &&
              typeof value === "object" &&
              typeof value.score === "number",
          )
          .map(([key, value]) => ({
            label: formatMetricLabel(key), // "total_words" → "Total Words"
            score: value.score,
            tag: value.label,
          }));
        setEvaluation({
          ...data,
          metrics,
          overallScore: data.fluency_score,
          feedbackMessage: data.overall_feedback,
        });
      }
    } catch (e) {
      console.error("Evaluation Error:", e);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    passage,
    evaluation,
    fetchPassage,
    startRecording,
    stopRecording,
    evaluateSpeech,
    setEvaluation,
  };
};

export default useHandler;
